import { FC, PropsWithChildren, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isTrackableRoute, HEARTBEAT_INTERVAL_MS, IDLE_TIMEOUT_MS } from "@/constants/studyTimeTracking";
import { studyTimeTrackingService } from "@/infrastructure/services";
import { isAuth } from "@/utils/api";

/**
 * Study Time Tracking Provider
 *
 * Silently monitors route changes and sends heartbeats to the backend
 * when the user is on a trackable study route.
 *
 * Tracking pauses when:
 * - User navigates to a non-trackable route
 * - Tab becomes hidden (Page Visibility API)
 * - User is idle for 5 minutes (no mouse/keyboard/touch/scroll)
 *
 * All state is stored in refs to avoid re-renders.
 * Must be placed inside the router tree (needs useLocation).
 */
export const StudyTimeTrackingProvider: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isIdleRef = useRef(false);
  const isVisibleRef = useRef(!document.hidden);
  const isTrackingRef = useRef(false);

  // --- Heartbeat sender ---
  const sendHeartbeat = useCallback(() => {
    if (!isAuth()) return;
    studyTimeTrackingService
      .sendHeartbeat({
        routePath: window.location.pathname,
        timestamp: new Date().toISOString(),
      })
      .catch(() => {
        // Silent fail; backend handles gaps via 90s threshold
      });
  }, []);

  // --- Start/stop tracking ---
  const startTracking = useCallback(() => {
    if (isTrackingRef.current) return;
    isTrackingRef.current = true;
    sendHeartbeat(); // Immediate first heartbeat
    intervalRef.current = setInterval(() => {
      if (!isIdleRef.current && isVisibleRef.current) {
        sendHeartbeat();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [sendHeartbeat]);

  const stopTracking = useCallback(() => {
    isTrackingRef.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // --- Route change detection ---
  useEffect(() => {
    if (isTrackableRoute(pathname)) {
      startTracking();
    } else {
      stopTracking();
    }
    return () => stopTracking();
  }, [pathname, startTracking, stopTracking]);

  // --- Page Visibility API ---
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      if (!document.hidden && isTrackableRoute(pathname) && !isIdleRef.current) {
        sendHeartbeat();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pathname, sendHeartbeat]);

  // --- Idle detection ---
  useEffect(() => {
    const resetIdleTimer = () => {
      isIdleRef.current = false;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        isIdleRef.current = true;
      }, IDLE_TIMEOUT_MS);
    };

    const events = ["mousedown", "keydown", "touchstart", "scroll"] as const;
    events.forEach((e) => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer(); // Initialize

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  // --- Cleanup on unmount ---
  useEffect(() => {
    return () => stopTracking();
  }, [stopTracking]);

  return <>{children}</>;
};
